/**
 * addressSearch — recherche de lieu, POINT D'ENTREE UNIQUE de l'application web.
 *
 * L'ORDRE, DECIDE PAR LE SERVEUR
 * ------------------------------
 *   1. Le repertoire interne (`sign` + `address`) est interroge D'ABORD.
 *   2. Le fournisseur externe n'est sollicite QUE si (1) ne rend rien.
 *   3. A la selection, {@link resolvePlace} enregistre le lieu retenu : le
 *      suivant qui le cherche est servi par (1), sans requete facturee.
 *
 * CE QUI A ETE RETIRE, ET POURQUOI
 * --------------------------------
 * Ce fichier empilait QUATRE couches, dont trois appelees depuis le NAVIGATEUR :
 *
 *   - Google Places, avec `REACT_APP_GOOGLE_MAP_API_KEY`. Une variable
 *     REACT_APP_* est inlinee dans le bundle a la compilation : la cle etait
 *     lisible par quiconque ouvre les outils de developpement. C'est pire que
 *     le cas de l'APK, ou il faut au moins decompresser l'archive.
 *
 *   - Nominatim (OpenStreetMap). Sa politique d'usage exige un User-Agent
 *     descriptif ; un navigateur INTERDIT de definir cet en-tete, et le code ne
 *     posait de toute facon que `Accept`. Chaque utilisateur tapait donc
 *     directement sur l'infrastructure d'OSM, depuis son IP, sans
 *     identification — la voie normale vers un blocage collectif.
 *
 *   - BAN (Base Adresse Nationale). Legitime et gratuite, mais elle repond aux
 *     ADRESSES, pas aux enseignes, et le serveur couvre deja les deux.
 *
 * Les trois faisaient doublon avec App\Services\PlaceSearch, qui applique la
 * meme regle cote serveur avec un cache partage : N utilisateurs tapant la meme
 * requete coutent UNE requete facturee au lieu de N.
 *
 * LE BUG QUE CETTE REECRITURE CORRIGE
 * -----------------------------------
 * L'autocompletion de Google ne renvoie AUCUNE geometrie — il faut un second
 * appel « Place Details » pour obtenir les coordonnees. Ce second appel
 * n'existait pas. Le formulaire envoyait donc `{ raw, place_id: "google-…" }`,
 * et cote API `Address::add()` ne declare que `raw`, `lat`, `lng` en
 * mass-assignment : `place_id` etait silencieusement ignore.
 *
 * Resultat : TOUTE adresse d'evenement creee depuis le web etait enregistree
 * SANS latitude ni longitude. Rien n'echouait, rien n'etait journalise — les
 * evenements etaient simplement invisibles sur une carte. Le meme genre de
 * perte silencieuse que le `long` / `lng` deja documente cote mobile.
 */
import axios from "../api/axios";

/**
 * Provenance d'une suggestion.
 *
 * 'internal' — prete a l'emploi : coordonnees et `address_id` deja remplis.
 * 'external' — porte un `place_id` et AUCUNE coordonnee. Passer par
 *              {@link resolvePlace} avant de s'en servir.
 */
export type AddressSource = "internal" | "external";

export interface AddressSuggestion {
  /** Identifiant stable : 'sign:<id>' ou 'google:<place_id>'. */
  place_id: string;
  /** Libelle complet affiche dans le champ. */
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  source: AddressSource;
  lat?: number;
  lng?: number;
  /** Id de la ligne `address`, present uniquement pour un resultat interne. */
  address_id?: number;
  /** Id du fournisseur, present uniquement pour un resultat externe. */
  provider_place_id?: string;
  /** Code centre partenaire, quand le lieu est reservable. */
  centre_code?: string;
  /** Distance en km, quand la position de l'appelant a ete transmise. */
  distance_km?: number;
}

export interface AddressSearchResult {
  suggestions: AddressSuggestion[];
  /**
   * Credit du fournisseur.
   *
   * Non nul UNIQUEMENT quand les resultats viennent du fournisseur externe,
   * dont les conditions d'utilisation imposent de l'afficher a cote d'eux. A
   * rendre verbatim, jamais traduit. Le champ existait deja cote API et n'etait
   * lu par personne.
   */
  attribution: string | null;
}

/** Le serveur ignore les requetes plus courtes. */
export const MIN_QUERY_LENGTH = 2;

const EMPTY: AddressSearchResult = { suggestions: [], attribution: null };

/**
 * Bascule sur les anciennes URL /event/address/* apres un 404, MEMORISEE.
 *
 * Le front peut etre deploye avant l'API. Sans ce repli, la recherche
 * d'adresse serait morte, sans message. Sans la memorisation, chaque frappe
 * paierait un 404 avant de retomber sur l'URL qui marche.
 */
let useLegacyEndpoints = false;

function isNotFound(error: any): boolean {
  return error?.response?.status === 404;
}

/** Coupe un libelle « 12 rue X, 75001 Paris » en titre + sous-titre. */
function splitLabel(raw: string): { main: string; secondary: string } {
  const parts = (raw ?? "").split(",");
  return {
    main: parts[0]?.trim() || raw || "",
    secondary: parts.slice(1).join(",").trim(),
  };
}

/**
 * Normalise la reponse du serveur.
 *
 * Le mapping precedent lisait `r.id` et `r.usage_count`, champs de l'ANCIEN
 * endpoint (qui interrogeait `address` jointe sur `event`). Depuis que
 * PlaceSearch l'a remplace, `id` est une chaine — 'sign:12' — et `usage_count`
 * n'existe plus : le badge « 3x » du menu deroulant ne s'affichait donc jamais,
 * et `internal_id` contenait une chaine la ou un nombre etait attendu.
 */
function toSuggestion(row: any): AddressSuggestion {
  const raw: string = row?.raw ?? row?.name ?? "";
  const name: string = row?.name ?? raw;
  const fallback = splitLabel(raw);

  return {
    place_id: String(row?.id ?? row?.place_id ?? raw),
    description: raw || name,
    structured_formatting: {
      main_text: name || fallback.main,
      // `secondary` n'est renseigne que par le fournisseur externe ; pour un
      // resultat interne on redecoupe le libelle complet.
      secondary_text: row?.secondary ?? (name === raw ? fallback.secondary : raw),
    },
    source: row?.source === "external" ? "external" : "internal",
    lat: row?.lat != null ? Number(row.lat) : undefined,
    lng: row?.lng != null ? Number(row.lng) : undefined,
    address_id: row?.address_id != null ? Number(row.address_id) : undefined,
    provider_place_id: row?.place_id ?? undefined,
    centre_code: row?.centre_code ?? undefined,
    distance_km: row?.distance_km != null ? Number(row.distance_km) : undefined,
  };
}

function toResult(payload: any): AddressSearchResult {
  const rows: any[] = Array.isArray(payload?.data) ? payload.data : [];

  return {
    suggestions: rows.map(toSuggestion),
    attribution: payload?.attribution ?? null,
  };
}

/**
 * Cherche un lieu.
 *
 * @param query  Ce que l'utilisateur a tape.
 * @param coords Position de l'appelant, optionnelle. Classe les resultats par
 *               proximite et oriente le fournisseur.
 */
export async function searchAddresses(
  query: string,
  coords?: { lat: number; lng: number } | null
): Promise<AddressSearchResult> {
  const q = (query ?? "").trim();
  if (q.length < MIN_QUERY_LENGTH) return EMPTY;

  const params: Record<string, string> = { q };

  // Paire COMPLETE uniquement, et (0, 0) rejete : c'est un point reel au large
  // du golfe de Guinee, mais surtout la valeur par defaut de bien des
  // geolocalisations non resolues. Classer par distance a ce point produirait
  // un ordre plausible et faux.
  if (
    coords &&
    Number.isFinite(coords.lat) &&
    Number.isFinite(coords.lng) &&
    !(coords.lat === 0 && coords.lng === 0)
  ) {
    params.lat = String(coords.lat);
    params.lng = String(coords.lng);
  }

  try {
    const url = useLegacyEndpoints ? "/event/address/search" : "/place/search";
    const res = await axios.get(url, { params });
    return toResult(res?.data);
  } catch (error) {
    if (!useLegacyEndpoints && isNotFound(error)) {
      useLegacyEndpoints = true;
      try {
        const res = await axios.get("/event/address/search", { params });
        return toResult(res?.data);
      } catch {
        return EMPTY;
      }
    }

    // Une recherche d'adresse en echec ne doit jamais bloquer la saisie d'un
    // evenement : l'utilisateur peut toujours taper son adresse a la main.
    return EMPTY;
  }
}

/** Taille de page demandee par les listes a chargement progressif. */
export const DEFAULT_PAGE_SIZE = 20;

export interface AddressSearchPage extends AddressSearchResult {
  page: number;
  /** Vrai s'il reste au moins une page a charger. */
  hasMore: boolean;
  /**
   * Nombre total de resultats internes. Renvoye en page 1 seulement (null
   * ensuite, ou sur une API anterieure a la pagination).
   */
  total: number | null;
}

export interface AddressSearchPageOptions {
  page?: number;
  perPage?: number;
  coords?: { lat: number; lng: number } | null;
  /** Annule la requete quand la saisie a change entre-temps. */
  signal?: AbortSignal;
}

/** Vrai si l'erreur vient d'une annulation volontaire (AbortController). */
export function isAbortError(error: any): boolean {
  return (
    error?.name === "CanceledError" ||
    error?.name === "AbortError" ||
    error?.code === "ERR_CANCELED"
  );
}

/**
 * Cherche UNE PAGE de lieux.
 *
 * Contrairement a {@link searchAddresses}, cette fonction LEVE en cas d'echec :
 * l'ecran doit pouvoir distinguer « aucun resultat » de « la recherche a
 * echoue » et proposer de reessayer. Avaler l'erreur en liste vide, c'est ce
 * qui faisait passer une panne pour « Aucun lieu trouve ».
 *
 * Retro-compatible : face a une API sans pagination (`meta` absent), la
 * premiere page est rendue telle quelle et `hasMore` vaut false.
 */
export async function searchAddressesPage(
  query: string,
  { page = 1, perPage = DEFAULT_PAGE_SIZE, coords, signal }: AddressSearchPageOptions = {}
): Promise<AddressSearchPage> {
  const q = (query ?? "").trim();
  if (q.length < MIN_QUERY_LENGTH) {
    return { ...EMPTY, page, hasMore: false, total: 0 };
  }

  const params: Record<string, string> = {
    q,
    page: String(page),
    per_page: String(perPage),
  };
  if (
    coords &&
    Number.isFinite(coords.lat) &&
    Number.isFinite(coords.lng) &&
    !(coords.lat === 0 && coords.lng === 0)
  ) {
    params.lat = String(coords.lat);
    params.lng = String(coords.lng);
  }

  const request = (url: string) => axios.get(url, { params, signal });

  let res;
  try {
    res = await request(useLegacyEndpoints ? "/event/address/search" : "/place/search");
  } catch (error) {
    if (!useLegacyEndpoints && isNotFound(error)) {
      useLegacyEndpoints = true;
      res = await request("/event/address/search");
    } else {
      throw error;
    }
  }

  const payload = res?.data;
  const base = toResult(payload);
  const meta = payload?.meta;

  return {
    ...base,
    page: Number(meta?.page ?? page),
    hasMore: meta ? Boolean(meta.has_more) : false,
    total: meta?.total != null ? Number(meta.total) : null,
  };
}

/**
 * Transforme une suggestion externe en lieu interne persiste.
 *
 * C'EST L'ETAPE QUI MANQUAIT. Sans elle, une suggestion externe n'a pas de
 * coordonnees et l'evenement est enregistre sans position. C'est aussi ce qui
 * fait grandir le repertoire : le lieu retenu est ecrit en base et repondra
 * desormais depuis la couche interne.
 *
 * Idempotent cote serveur (index unique sur le place id) : un double clic ne
 * peut pas creer deux enregistrements.
 */
export async function resolvePlace(providerPlaceId: string): Promise<AddressSuggestion | null> {
  try {
    const url = useLegacyEndpoints ? "/event/address/resolve" : "/place/resolve";
    const res = await axios.post(url, { place_id: providerPlaceId });
    return res?.data ? toSuggestion(res.data) : null;
  } catch (error) {
    if (!useLegacyEndpoints && isNotFound(error)) {
      useLegacyEndpoints = true;
      try {
        const res = await axios.post("/event/address/resolve", { place_id: providerPlaceId });
        return res?.data ? toSuggestion(res.data) : null;
      } catch {
        return null;
      }
    }
    return null;
  }
}
